import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import CompetitionSelector from '../components/CompetitionSelector';
import SeasonSelector from '../components/SeasonSelector';
import TeamsList from '../components/TeamsList';
import MatchesList from '../components/MatchesList';
import AppLauncher from '../components/AppLauncher';

function teamDisplay(team) {
  if (!team) return '?';
  if (typeof team === 'string') return team;
  return team.acronymName || team.shortName || team.officialName || '?';
}

function matchLabel(match) {
  if (!match) return '';
  const home = teamDisplay(match?.home || match?.homeTeam);
  const away = teamDisplay(match?.away || match?.awayTeam);
  return `${home} vs ${away}`;
}

/**
 * Serializza lo stato per history (esclude oggetti troppo grandi se necessario).
 * I dati match/season/competition vengono salvati nello history state del browser.
 */
function pushNav(state) {
  window.history.pushState(state, '', window.location.pathname);
}

export default function DashboardPage() {
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedSeason,      setSelectedSeason]      = useState(null);
  const [selectedMatch,       setSelectedMatch]        = useState(null);
  const [activeTab,           setActiveTab]            = useState('matches');

  // Determina lo step corrente dallo stato
  const step = selectedMatch       ? 'match'
             : selectedSeason      ? 'dashboard'
             : selectedCompetition ? 'season'
             :                       'competition';

  // ── Ripristina stato dal tasto Indietro/Avanti ───────────────────
  const restoreState = useCallback((state) => {
    if (!state) {
      setSelectedCompetition(null);
      setSelectedSeason(null);
      setSelectedMatch(null);
      setActiveTab('matches');
      return;
    }
    setSelectedCompetition(state.competition || null);
    setSelectedSeason(state.season           || null);
    setSelectedMatch(state.match             || null);
    setActiveTab(state.tab                   || 'matches');
  }, []);

  useEffect(() => {
    // Salva lo stato iniziale (root) nello history
    if (!window.history.state) {
      window.history.replaceState({ competition: null, season: null, match: null, tab: 'matches' }, '');
    }

    const onPop = (e) => restoreState(e.state);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [restoreState]);

  // ── Handlers ────────────────────────────────────────────────────
  function handleSelectCompetition(comp) {
    setSelectedCompetition(comp);
    setSelectedSeason(null);
    setSelectedMatch(null);
    setActiveTab('matches');
    pushNav({ competition: comp, season: null, match: null, tab: 'matches' });
  }

  function handleSelectSeason(season) {
    setSelectedSeason(season);
    setSelectedMatch(null);
    setActiveTab('matches');
    pushNav({ competition: selectedCompetition, season, match: null, tab: 'matches' });
  }

  function handleSelectMatch(match) {
    setSelectedMatch(match);
    pushNav({ competition: selectedCompetition, season: selectedSeason, match, tab: activeTab });
  }

  function handleTabChange(tab) {
    setActiveTab(tab);
    pushNav({ competition: selectedCompetition, season: selectedSeason, match: null, tab });
  }

  // ── Breadcrumbs ─────────────────────────────────────────────────
  const breadcrumbs = [
    {
      label: 'Competizioni',
      onClick: step !== 'competition' ? () => {
        setSelectedCompetition(null);
        setSelectedSeason(null);
        setSelectedMatch(null);
        pushNav({ competition: null, season: null, match: null, tab: 'matches' });
      } : null,
    },
  ];

  if (selectedCompetition) {
    breadcrumbs.push({
      label: selectedCompetition.acronymName || selectedCompetition.officialName || selectedCompetition.name,
      onClick: step !== 'season' ? () => {
        setSelectedSeason(null);
        setSelectedMatch(null);
        pushNav({ competition: selectedCompetition, season: null, match: null, tab: 'matches' });
      } : null,
    });
  }

  if (selectedSeason) {
    breadcrumbs.push({
      label: selectedSeason.seasonName || selectedSeason.name,
      onClick: step !== 'dashboard' ? () => {
        setSelectedMatch(null);
        pushNav({ competition: selectedCompetition, season: selectedSeason, match: null, tab: activeTab });
      } : null,
    });
  }

  if (selectedMatch) {
    breadcrumbs.push({ label: matchLabel(selectedMatch) });
  }

  return (
    <div className="app-layout">
      <Header breadcrumbs={breadcrumbs} />

      <main className="app-main">
        {step === 'competition' && (
          <CompetitionSelector onSelect={handleSelectCompetition} />
        )}

        {step === 'season' && selectedCompetition && (
          <SeasonSelector competition={selectedCompetition} onSelect={handleSelectSeason} />
        )}

        {step === 'dashboard' && selectedSeason && (
          <>
            <div className="tab-bar">
              <button className={`tab-btn ${activeTab === 'matches' ? 'active' : ''}`}
                onClick={() => handleTabChange('matches')}>Partite</button>
              <button className={`tab-btn ${activeTab === 'teams' ? 'active' : ''}`}
                onClick={() => handleTabChange('teams')}>Squadre</button>
            </div>
            {activeTab === 'matches' && (
              <MatchesList season={selectedSeason} onSelectMatch={handleSelectMatch} />
            )}
            {activeTab === 'teams' && (
              <TeamsList season={selectedSeason} />
            )}
          </>
        )}

        {step === 'match' && selectedMatch && (
          <AppLauncher match={selectedMatch} />
        )}
      </main>
    </div>
  );
}
